import dayjs from 'utils/dayjs';

import { currencies } from 'constants/currency';
import {
  request,
  callApi,
  capitalize,
  DEFAULT_MOBILE_DATE_FORMAT,
  filterExcludedCurrencies,
  getSharePreviewImageUrl,
  parseDate,
} from 'utils';
import {
  mapMbArchiveAvgItems,
  normalizeMbArchiveRange,
  toMbArchiveAvgDateParam,
} from 'utils/mbArchive';
import {
  buildCurrencyYearArchiveFromApiResponses,
  getCurrencyYearArchiveMinYear,
} from 'utils/currencyYearArchive';
import { CURRENCY_YEAR_ARCHIVE_SOURCE } from 'constants/currencyYearArchive';
import {
  FETCH_MB_CURRENCY,
  FETCH_MB_COMMENTS,
  FETCH_MB_ARCHIVE,
  FETCH_CURRENCY_YEAR_ARCHIVE,
  FETCH_NBU_CURRENCY_LIST,
  FETCH_ALL_CURRENCY_LIST,
  FETCH_ALL_CITIES_LIST,
  FETCH_NEWS,
  FETCH_VIDEOS,
  FETCH_DISCUSSIONS,
  FETCH_ARTICLES,
  FETCH_COMPANIES_NEWS,
  FETCH_VA_CURRENCY_LIST,
} from 'actionTypes';

export const fetchMbCurrency = (props = null) => {
  const dateRequest = props?.date || '';

  const action = () =>
    request({
      url: `${process.env.APP_MB_API_URL}/3183024733/${dateRequest}`,
    }).then(response => {
      const acc = {
        [currencies.USD]: null,
        [currencies.EUR]: null,
      };

      const whiteList = new Map(
        [currencies.USD, currencies.EUR].map(item => [item, item])
      );

      response
        .filter(({ currency }) => whiteList.has(currency))
        .forEach(item => {
          const key = item.currency;
          if (acc[key] === null) {
            acc[key] = item;
            return;
          }
          const currentDate = parseDate(acc[key].date);
          const nextDate = parseDate(item.date);

          if (currentDate && nextDate && currentDate.isBefore(nextDate)) {
            acc[key] = item;
          }
        });
      return Object.values(acc);
    });

  return callApi(action, FETCH_MB_CURRENCY);
};

export const fetchCurrentNbuCurr = ({ date, searchText, locale }) => {
  return request({
    url: date
      ? `${process.env.APP_NBU_RATES_API_URL}/?date=${date}${searchText ||
          ''}&locale=${locale}`
      : `${process.env.APP_NBU_RATES_API_URL}${searchText ||
          ''}locale=${locale}`,
    method: 'get',
  });
};

export const fetchNbuCurr = ({ date, locale }) => {
  const action = () =>
    request({
      url: process.env.APP_NBU_RATES_API_URL,
      method: 'get',
      params: {
        locale,
        date,
      },
    });

  return callApi(action, FETCH_NBU_CURRENCY_LIST);
};

export const fetchMbComments = params => {
  const action = () =>
    request({
      url: `${process.env.APP_MINFIN_API_DOMAIN}/api/currency/rates/interbank/?locale=${params.intl.locale}`,
    });

  return callApi(action, FETCH_MB_COMMENTS);
};

const fetchMbInterbankByDate = (params, date) =>
  request({
    url: `${process.env.APP_MINFIN_API_DOMAIN}/api/currency/rates/interbank/?locale=${params.intl.locale}&date=${date}`,
  });

const fetchMbInterbankAvgRange = (currency, from, to) =>
  request({
    url: `${
      process.env.APP_CURRENCIES_API_URL
    }/rates/interbank/${currency.toLowerCase()}/avg`,
    params: {
      period: 'day',
      from,
      to,
    },
  });

export const fetchMbArchive = params => {
  const action = async () => {
    const { date, dateFrom, dateTo } = params.match?.params || {};
    const emptyArchiveData = {
      [currencies.USD.toUpperCase()]: [],
      [currencies.EUR.toUpperCase()]: [],
    };

    if (dateTo) {
      const normalizedRange = normalizeMbArchiveRange(dateFrom, dateTo);

      if (!normalizedRange) {
        return {
          mode: 'range',
          date: dateFrom,
          dateTo,
          data: emptyArchiveData,
        };
      }

      const { dateFrom: rangeFrom, dateTo: rangeTo } = normalizedRange;
      const from = toMbArchiveAvgDateParam(rangeFrom);
      const to = toMbArchiveAvgDateParam(rangeTo);

      const [usdResponse, eurResponse] = await Promise.all([
        fetchMbInterbankAvgRange(currencies.USD, from, to),
        fetchMbInterbankAvgRange(currencies.EUR, from, to),
      ]);

      return {
        mode: 'range',
        date: rangeFrom,
        dateTo: rangeTo,
        data: {
          [currencies.USD.toUpperCase()]: mapMbArchiveAvgItems(
            usdResponse.data?.[currencies.USD.toUpperCase()]
          ),
          [currencies.EUR.toUpperCase()]: mapMbArchiveAvgItems(
            eurResponse.data?.[currencies.EUR.toUpperCase()]
          ),
        },
      };
    }

    const archiveDate = date || dateFrom;
    const response = await fetchMbInterbankByDate(params, archiveDate);

    return {
      mode: 'day',
      date: archiveDate,
      dateTo: null,
      data: response.data || emptyArchiveData,
    };
  };

  return callApi(action, FETCH_MB_ARCHIVE);
};

const CURRENCY_YEAR_ARCHIVE_API_SOURCES = Object.values(
  CURRENCY_YEAR_ARCHIVE_SOURCE
);

const fetchCurrencyYearArchiveSource = (source, currency, year) =>
  request({
    url: `${process.env.APP_MINFIN_API_DOMAIN}/api/currency/archive/${source}/`,
    params: {
      year,
      currency: currency.toUpperCase(),
    },
  }).catch(error => {
    console.error('fetchCurrencyYearArchive source error', source, error);
    return null;
  });

export const fetchCurrencyYearArchive = params => {
  const action = async () => {
    const { currency, year } = params.match?.params || {};
    const normalizedYear = Number.parseInt(String(year), 10);
    const currentYear = new Date().getFullYear();
    const minYear = getCurrencyYearArchiveMinYear();

    if (
      !currency ||
      !Number.isInteger(normalizedYear) ||
      normalizedYear > currentYear ||
      normalizedYear < minYear
    ) {
      return null;
    }

    const results = await Promise.allSettled(
      CURRENCY_YEAR_ARCHIVE_API_SOURCES.map(source =>
        fetchCurrencyYearArchiveSource(source, currency, normalizedYear)
      )
    );

    const responsesBySource = CURRENCY_YEAR_ARCHIVE_API_SOURCES.reduce(
      (acc, source, index) => {
        const result = results[index];
        acc[source] = result.status === 'fulfilled' ? result.value : null;
        return acc;
      },
      {}
    );

    return buildCurrencyYearArchiveFromApiResponses(
      currency,
      normalizedYear,
      responsesBySource
    );
  };

  return callApi(action, FETCH_CURRENCY_YEAR_ARCHIVE);
};

export const fetchAllCurrencyList = ({ type, locale = 'uk' }) => {
  const action = () =>
    request({
      url: `${process.env.APP_CURRENCIES_API_URL}/list/`,
      method: 'get',
      params: { locale, type },
    }).then(({ list }) =>
      filterExcludedCurrencies(list).map(item => ({
        id: item.code,
        name: item.title,
        text: item.name,
        nameG: item.nameG,
        path: `/currency/${item.code.toLowerCase()}`,
      }))
    );

  return callApi(action, FETCH_ALL_CURRENCY_LIST);
};

export const fetchVaCurrencyList = ({ intl: { locale } }) => {
  const action = () =>
    request({
      url: `${process.env.APP_CURRENCIES_API_URL}/list?va-list=true&type=money`,
      method: 'get',
      params: { locale, type: 'money' },
    }).then(({ list }) =>
      filterExcludedCurrencies(list).map(item => ({
        id: item.code,
        name: item.title,
        text: item.name,
        nameG: item.nameG,
        path: `/currency/${item.code.toLowerCase()}`,
      }))
    );

  return callApi(action, FETCH_VA_CURRENCY_LIST);
};

export const fetchAllCitiesList = ({ intl: { locale } }) => {
  const action = () =>
    request({
      url: process.env.APP_GEO_API_URL,
      method: 'get',
    }).then(data => {
      const entities = {};
      const ids = [];

      data.forEach(({ cities }) => {
        cities.forEach(city => {
          const dumpCity = {
            id: parseInt(city.id, 10),
            label: city[`name${capitalize(locale)}`],
            nameP: city[`nameP${capitalize(locale)}`],
            nameG: city[`nameG${capitalize(locale)}`],
            value: city.slug?.toLowerCase(),
          };

          entities[dumpCity.value] = dumpCity;
          ids.push(dumpCity.value);
        });
      });

      return { entities, ids };
    });

  return callApi(action, FETCH_ALL_CITIES_LIST);
};

export const fetchNews = ({ intl: { locale } }) => {
  const action = () =>
    request({
      url: `${process.env.APP_NEWS_API_URL}/list/?section%5B%5D=2&type=1&cpp=4&locale=${locale}`,
      method: 'get',
    });

  return callApi(action, FETCH_NEWS);
};

export const fetchCompaniesNews = ({ intl: { locale } }) => {
  const action = () =>
    request({
      url: `${process.env.APP_NEWS_API_URL}/list/?section[]=2&section[]=13&section[]=14&status=company&cpp=7&locale=${locale}`,
      method: 'get',
    });

  return callApi(action, FETCH_COMPANIES_NEWS);
};

export const fetchArticles = ({ intl: { locale } }) => {
  const action = () =>
    request({
      url: `${process.env.APP_ARTICLES_API_URL}/list/?start=0&end=2&locale=${locale}`,
      method: 'get',
    }).then(response => {
      return response?.blocks || [];
    });

  return callApi(action, FETCH_ARTICLES);
};

export const fetchVideos = (
  { isLoadMore, page } = { isLoadMore: false, page: 1 }
) => {
  const pid = 'PLBrs-SZovhB6ESZJhCX4Q8IseUrbsJYnh';
  const emptyVideosPayload = {
    items: [],
    page: page || 1,
    totalPages: 0,
    total: 0,
  };

  const action = () =>
    request({
      url: `${process.env.APP_YOUTUBE_API}/playlists/${pid}/videos`,
      method: 'get',
      params: { page },
    })
      .then(response => {
        return {
          ...response,
          items:
            response.items?.map(item => {
              const { thumbnails, ...restItem } = item;
              return {
                ...restItem,
                thumbnailUrl: thumbnails?.medium?.url,
              };
            }) || [],
        };
      })
      .catch(() => {
        return emptyVideosPayload;
      });

  return callApi(action, FETCH_VIDEOS, { isLoadMore });
};

// eslint-disable-next-line consistent-return
export const fetchShareRateCurrency = async () => {
  return {
    url: getSharePreviewImageUrl(),
    generatedAt: '',
    fallbackUsed: false,
  };
};

export const fetchDiscussions = date => {
  const currentDate = dayjs().format(DEFAULT_MOBILE_DATE_FORMAT);

  const action = () =>
    request({
      url: `${process.env.APP_CURRENCIES_API_URL}/discussion`,
      method: 'get',
      params: date ? { date } : { date: currentDate },
    });

  return callApi(action, FETCH_DISCUSSIONS);
};
